// src/components/Banks.jsx
import React, { useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import HeroLogo from "../images/hero-fincorp.webp";
import BajajLogo from "../images/bajaj-logo.jpg";
import KotakLogo from "../images/kotak-logo.svg";

/* Outer container with horizontal padding + center align (dark theme aware) */
const Container = styled.section`
  text-align: center;
  padding-left: 20px;
  padding-right: 20px;
  color: ${(p) => p.theme.colors?.text || "#ffffff"};
  background: ${(p) =>
    p.theme.gradients?.bgSection ||
    `linear-gradient(180deg, ${p.theme.colors?.bgDark || "#0A0F1D"}, ${
      p.theme.colors?.bgDarker || "#02050A"
    } 100%)`};
  padding-top: 28px;
  padding-bottom: 28px;

  @media (min-width: 720px) {
    padding-left: 32px;
    padding-right: 32px;
    padding-top: 36px;
    padding-bottom: 36px;
  }

  h2 {
    color: ${(p) => p.theme.colors?.text || "#fff"};
    margin: 0 0 6px 0;
  }

  p.lead {
    color: ${(p) => p.theme.colors?.muted || "#A7B1C2"};
    margin: 0;
  }
`;

/* Vertical list container (centered column) */
const List = styled(motion.ul)`
  list-style: none;
  margin: 20px auto 0;
  padding: 0;
  width: 100%;
  max-width: 760px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

/* Animated card per bank */
const BankCard = styled(motion.li)`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px;
  border-radius: 12px;
  background: ${(p) =>
    p.theme.colors?.card ||
    "linear-gradient(90deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01))"};
  border: 1px solid
    ${(p) => p.theme.colors?.cardBorder || "rgba(255,255,255,0.03)"};
  box-shadow: 0 10px 30px rgba(2, 6, 23, 0.28);
`;

/* logo box */
const LogoBox = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  background: ${(p) => p.bg || "linear-gradient(90deg,#0b64d1,#3aa0ff)"};
  overflow: hidden;
`;

/* logo image */
const LogoImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  background: transparent;
`;

/* initials fallback */
const Initials = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
  place-items: center;
  font-weight: 800;
  font-size: 1.05rem;
  color: ${(p) => p.theme.colors?.text || "#fff"};
`;

/* meta */
const Meta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const BankName = styled.div`
  font-weight: 800;
  font-size: 1rem;
  color: ${(p) => p.theme.colors?.text || "#fff"};
`;

const TimeBadge = styled.span`
  margin-top: 6px;
  font-size: 0.78rem;
  padding: 6px 10px;
  background: rgba(255, 255, 255, 0.03);
  color: ${(p) => p.theme.colors?.muted || "#A7B1C2"};
  border-radius: 999px;
  font-weight: 700;
`;

/* right arrow */
const Chevron = styled.svg`
  margin-left: auto;
  width: 18px;
  height: 18px;
  stroke: ${(p) => p.theme.colors?.accent || "#4A00E0"};
  flex-shrink: 0;
`;

/* note */
const Note = styled.p`
  margin-top: 18px;
  color: ${(p) => p.theme.colors?.muted || "#A7B1C2"};
  font-size: 0.9rem;
`;

/* local data */
const BANKS = [
  {
    name: "Hero Fincorp",
    start: "2020",
    end: null,
    initials: "HF",
    color: "linear-gradient(90deg,#0b64d1,#3aa0ff)",
    logo: HeroLogo,
    alt: "Hero Fincorp logo",
  },
  {
    name: "Kotak Mahindra",
    start: "2020",
    end: "2023",
    initials: "KM",
    color: "linear-gradient(90deg,#ff7a45,#ffb07a)",
    logo: KotakLogo,
    alt: "Kotak Mahindra logo",
  },
  {
    name: "Bajaj Finance Ltd.",
    start: "Apr 2024",
    end: "Nov 2024",
    initials: "BF",
    color: "linear-gradient(90deg,#1db954,#6ce28a)",
    logo: BajajLogo,
    alt: "Bajaj Finance logo",
  },
];

/* framer-motion variants for parent + items */
const listVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.48, ease: [0.2, 0.9, 0.2, 1] },
  },
};

function BankItem({ bank }) {
  // track image load/error state so we only render image when valid
  const [imgOk, setImgOk] = useState(true);

  return (
    <BankCard variants={itemVariants}>
      <LogoBox bg={bank.color} aria-hidden>
        {bank.logo && imgOk ? (
          <LogoImg
            src={bank.logo}
            alt={bank.alt}
            onError={() => setImgOk(false)}
            onLoad={() => setImgOk(true)}
          />
        ) : (
          <Initials>{bank.initials}</Initials>
        )}
      </LogoBox>

      <Meta>
        <BankName>{bank.name}</BankName>
        <TimeBadge>
          {bank.end ? `${bank.start} — ${bank.end}` : `${bank.start} — Present`}
        </TimeBadge>
      </Meta>

      <Chevron viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M9 6l6 6-6 6"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </Chevron>
    </BankCard>
  );
}

export default function Banks({ partners = [] }) {
  const visibleBanks = partners.length
    ? partners.map((partner) => ({
        ...partner,
        initials: partner.name?.slice(0, 2).toUpperCase() || "PR",
        logo: partner.logo || null,
        alt: `${partner.name} logo`,
        color: "linear-gradient(90deg,#0b64d1,#3aa0ff)",
        start: partner.category || "Partner",
        end: null,
      }))
    : BANKS;

  return (
    <Container id="banks" aria-labelledby="banks-heading">
      <h2 id="banks-heading">Worked with</h2>

      <p className="lead">{`Trusted partners across retail & institutional lending.`}</p>

      <List
        initial="hidden"
        animate="visible"
        variants={listVariants}
        role="list"
        aria-label="Partner logos and engagement dates"
      >
        {visibleBanks.map((b) => (
          <BankItem key={b.name} bank={b} />
        ))}
      </List>

      <Note>Dates represent the duration of engagement.</Note>
    </Container>
  );
}
